/**
 * The two branches, as one switchable thing.
 *
 * A ruleset is everything the charts need that the branches disagree about:
 * the feeding equation, whether instant threat exists at all, how passive
 * income is derived, and what a point of threat buys. Everything they agree
 * about — the raffle, the health curve, group sizes, flask values, the
 * difficulty ladder — stays where it was and is not duplicated here.
 *
 * Every ruleset-dependent entry point takes the ruleset explicitly. There is no
 * default and no ambient "current branch" in the model: a caller that forgets to
 * say which branch it means fails to compile rather than quietly reporting the
 * live numbers for a test-branch question.
 */

import {
  DEFAULTS,
  TESTING_COMBAT_CHANGES,
  TESTING_FEED,
  TESTING_THREAT_VALUES,
  THREAT_VALUES,
  type CombatModifierChange,
} from './constants';
import {
  THREAT_TICKS_PER_MINUTE,
  bisectMutagenForEvo,
  biterHealthFactor,
  calcFeedEffects,
  calcFeedEffectsTesting,
  evoRate,
  playerCountModifier,
  testingEvoForMutagen,
  testingMutagenForEvo,
  threatRate,
  type FeedEffects,
} from './feeding';
import {
  DEFAULT_SCENARIO_PARAMS,
  scenarioEvoForMutagen,
  scenarioEvoForPassive,
  scenarioKey,
  scenarioMutagenForEvo,
  scenarioPassivePerSecond,
  scenarioPassiveSlope,
  type ScenarioParams,
} from './scenario';

export type RulesetId = 'live' | 'testing3v3' | 'scenario';

export interface Ruleset {
  id: RulesetId;
  /**
   * Identity for anything cached per branch. Equal to `id` for the transcribed
   * branches, whose equations are frozen; the tuning scenario folds its whole
   * parameter set in, because moving a slider makes it a different ruleset and
   * a cache keyed on `id` alone would keep serving the previous curve.
   */
  cacheKey: string;
  /** Short label for the branch switch. */
  label: string;
  /** Git branch these numbers are transcribed from. */
  branch: string;
  /** Where that branch lives, for the "ported from" line. */
  origin: string;
  /** One line, shown next to the switch. */
  tagline: string;
  /**
   * False when `calc_feed_effects` returns `threat_increase = 0` for every
   * input. The instant-threat chart, its rail and its editor are all suppressed
   * rather than drawn as a flat zero.
   */
  hasInstantThreat: boolean;
  /** False when the connected-player count enters no equation at all. */
  usesPlayerCount: boolean;
  /** What one point of threat means here, for axis and readout labels. */
  threatUnit: string;
  /** `threat_values` — the price of each unit, in this branch's threat units. */
  threatValues: Record<string, number>;
  /** Combat modifiers this branch moves that the lab does not chart. */
  combatChanges: CombatModifierChange[];

  /** `Public.calc_feed_effects`, as this branch implements it. */
  calcFeedEffects(
    initialEvo: number,
    foodValue: number,
    numFlasks: number,
    currentPlayerCount: number,
    maxReanimThresh: number,
  ): FeedEffects;

  /** Mutagen that carries `startEvo` to `targetEvo`. */
  mutagenForEvo(targetEvo: number, startEvo?: number): number;

  /**
   * Evolution after feeding `mutagen` from zero. The inverse of `mutagenForEvo`,
   * and what re-expresses a mutagen window as an evolution window — which is
   * what switching branches does with the zoom and the selection.
   */
  evoForMutagen(mutagen: number): number;

  /** Inverse of the passive-income curve: mutagen from evo 0 for `perMinute`. */
  mutagenForPassivePerMinute(perMinute: number): number;

  /**
   * Passive threat per minute once evolution has reached `evo`, fed from zero.
   * The forward of `mutagenForPassivePerMinute`, and stated per branch because
   * deriving it generically would mean a bisection per point — this is called
   * once per sample of an evolution sweep, where that cost is real.
   */
  passivePerMinuteAtEvo(evo: number): number;

  /**
   * `d(instant threat)/d(mutagen)` at `evo`, player modifier already applied —
   * the instant threat the *next* unit of mutagen delivers.
   *
   * Stated as a slope rather than read off the cumulative curve because the
   * ratio chart needs it exactly: differencing a sampled curve would make the
   * ratio depend on the sample spacing, and therefore on the zoom.
   */
  instantSlope(evo: number, playerCount: number): number;

  /** `d(passive threat per second)/d(mutagen)` at `evo`. */
  passiveSlope(evo: number): number;
}

/**
 * Minutes of passive income that one flask's instant threat is worth, at the
 * margin — the number the balance pass is actually tuning.
 *
 * Both slopes are per unit of mutagen, so the mutagen cancels: what is left is
 * "threat landed now" over "threat per minute gained", which is a time. Ten
 * means a send hits like ten minutes of the income it also buys.
 *
 * Marginal, not cumulative. The cumulative version answers "what has everything
 * fed so far been worth", which is dominated by the cheap early evolution and
 * says little about whether the next send is worth making.
 */
export function marginalRatioMinutes(
  ruleset: Ruleset,
  evo: number,
  playerCount: number,
): number {
  const passivePerMinute = ruleset.passiveSlope(evo) * THREAT_TICKS_PER_MINUTE;
  if (!(passivePerMinute > 0) || !Number.isFinite(passivePerMinute)) return 0;
  const instant = ruleset.instantSlope(evo, playerCount);
  return Number.isFinite(instant) ? instant / passivePerMinute : 0;
}

/**
 * Feed `mutagen` in a single send and report the resulting state. This is the
 * exact call the game makes, with `foodValue = mutagen` and `numFlasks = 1`
 * (the same trick `/calc-send` uses in feeding_calculations.lua).
 */
export function feedMutagen(
  ruleset: Ruleset,
  initialEvo: number,
  mutagen: number,
  playerCount: number,
  maxReanimThresh = DEFAULTS.maxReanimThresh,
): FeedEffects {
  if (mutagen <= 0) {
    // Not a shortcut for speed: a zero send must still report the income and
    // health factor that *already* apply at `initialEvo`, and on the test
    // branch that income is not derivable from evolution alone.
    return ruleset.calcFeedEffects(initialEvo, 0, 1, playerCount, maxReanimThresh);
  }
  return ruleset.calcFeedEffects(initialEvo, mutagen, 1, playerCount, maxReanimThresh);
}

/** Mutagen that carries `startEvo` to `targetEvo` under `ruleset`. */
export function mutagenToReachEvo(ruleset: Ruleset, targetEvo: number, startEvo = 0): number {
  return ruleset.mutagenForEvo(targetEvo, startEvo);
}

/** `storage.bb_threat_income * 60` — the number the team GUI prints. */
export function threatIncomePerMinute(effects: FeedEffects): number {
  return effects.passiveThreatPerSecond * THREAT_TICKS_PER_MINUTE;
}

/* ------------------------------------------------------------------ branches */

/** `master` — what the public servers run. */
export const LIVE: Ruleset = {
  id: 'live',
  cacheKey: 'live',
  label: 'Live',
  branch: 'master',
  origin: 'Factorio-Biter-Battles/Factorio-Biter-Battles',
  tagline: 'the scenario the public servers run',
  hasInstantThreat: true,
  usesPlayerCount: true,
  threatUnit: 'threat',
  threatValues: THREAT_VALUES,
  combatChanges: [],
  calcFeedEffects,
  mutagenForEvo: (targetEvo, startEvo = 0) =>
    bisectMutagenForEvo(
      (initialEvo, mutagen) =>
        initialEvo +
        calcFeedEffects(initialEvo, mutagen, 1, DEFAULTS.playerCount, DEFAULTS.maxReanimThresh)
          .evoIncrease,
      targetEvo,
      startEvo,
    ),
  evoForMutagen: (mutagen) =>
    mutagen <= 0
      ? 0
      : calcFeedEffects(0, mutagen, 1, DEFAULTS.playerCount, DEFAULTS.maxReanimThresh).evoIncrease,
  // Income is `evo * 25` per second, so the inverse is an evolution lookup.
  mutagenForPassivePerMinute: (perMinute) =>
    LIVE.mutagenForEvo(
      perMinute / (DEFAULTS.threatIncomePerEvoPerSecond * THREAT_TICKS_PER_MINUTE),
      0,
    ),
  passivePerMinuteAtEvo: (evo) =>
    Math.max(evo, 0) * DEFAULTS.threatIncomePerEvoPerSecond * THREAT_TICKS_PER_MINUTE,
  // Both slopes are the loop's own per-iteration rates, read at one evolution
  // instead of summed over a send — which is exactly what the loop integrates.
  instantSlope: (evo, playerCount) => threatRate(evo) * playerCountModifier(playerCount),
  // Income is `25 · E`, so its slope in mutagen is 25 · dE/dM.
  passiveSlope: (evo) => DEFAULTS.threatIncomePerEvoPerSecond * evoRate(evo),
};

/** `cojito_3v3_balance_testing` — the 3v3 balance test. */
export const TESTING_3V3: Ruleset = {
  id: 'testing3v3',
  cacheKey: 'testing3v3',
  label: '3v3 test',
  branch: 'cojito_3v3_balance_testing',
  origin: 'cojito-factorio/Factorio-Biter-Battles',
  tagline: 'no instant threat; passive income linear in mutagen',
  hasInstantThreat: false,
  usesPlayerCount: false,
  threatUnit: 'threat ≈ small biters',
  threatValues: TESTING_THREAT_VALUES,
  combatChanges: TESTING_COMBAT_CHANGES,
  calcFeedEffects: calcFeedEffectsTesting,
  // Closed form both ways, so no search: E = (M / scale) ^ power inverts exactly.
  mutagenForEvo: (targetEvo, startEvo = 0) =>
    targetEvo <= startEvo
      ? 0
      : testingMutagenForEvo(targetEvo) - testingMutagenForEvo(Math.max(startEvo, 0)),
  evoForMutagen: testingEvoForMutagen,
  // Income is `total_mutagen * 1.5` per second — linear in mutagen, not in evo.
  mutagenForPassivePerMinute: (perMinute) =>
    Math.max(perMinute, 0) / (TESTING_FEED.smallBitersPerFood * THREAT_TICKS_PER_MINUTE),
  passivePerMinuteAtEvo: (evo) =>
    testingMutagenForEvo(Math.max(evo, 0)) *
    TESTING_FEED.smallBitersPerFood *
    THREAT_TICKS_PER_MINUTE,
  instantSlope: () => 0,
  // Income is `1.5 · M`, already linear in mutagen, so the slope is the constant.
  passiveSlope: () => TESTING_FEED.smallBitersPerFood,
};

/* -------------------------------------------------------------------------- */
/*  the tuning scenario                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Build a ruleset from a parameter set. Every call returns a fresh object, so
 * the identity of the result is the identity of the parameters — memoise it
 * where you hold the parameters, and everything downstream that keys on
 * `cacheKey` stays honest by construction.
 *
 * Threat is priced in the branch's `small = 15` units (ai.lua), so one point is
 * one small biter, the same as the 3v3 test and unlike live.
 */
export function makeScenarioRuleset(params: ScenarioParams): Ruleset {
  const evoForMutagen = (mutagen: number) => scenarioEvoForMutagen(mutagen, params);
  const passivePerSecond = (evo: number) => scenarioPassivePerSecond(evo, params);

  return {
    id: 'scenario',
    cacheKey: `scenario:${scenarioKey(params)}`,
    label: 'Scenario',
    branch: 'proposal',
    origin: 'tuned here',
    tagline: 'the tuning target — every constant on a control',
    hasInstantThreat: params.instantScale > 0,
    // The instant scale is a flat constant per mutagen: no crowd modifier, so
    // a send is worth the same whether six are on or sixty.
    usesPlayerCount: false,
    threatUnit: 'threat ≈ small biters',
    threatValues: TESTING_THREAT_VALUES,
    combatChanges: [],

    calcFeedEffects: (initialEvo, foodValue, numFlasks, _playerCount, maxReanimThresh) => {
      const food = foodValue * numFlasks;
      const totalFood = scenarioMutagenForEvo(initialEvo, params) + food;
      const evo = evoForMutagen(totalFood);
      return {
        evoIncrease: evo - initialEvo,
        threatIncrease: params.instantScale * food,
        biterHealthFactor: biterHealthFactor(evo, maxReanimThresh),
        passiveThreatPerSecond: passivePerSecond(evo),
      };
    },

    mutagenForEvo: (targetEvo, startEvo = 0) =>
      targetEvo <= startEvo
        ? 0
        : scenarioMutagenForEvo(targetEvo, params) -
          scenarioMutagenForEvo(Math.max(startEvo, 0), params),
    evoForMutagen,
    mutagenForPassivePerMinute: (perMinute) =>
      scenarioMutagenForEvo(
        scenarioEvoForPassive(Math.max(perMinute, 0) / THREAT_TICKS_PER_MINUTE, params),
        params,
      ),
    passivePerMinuteAtEvo: (evo) => passivePerSecond(Math.max(evo, 0)) * THREAT_TICKS_PER_MINUTE,

    // `T = c · M` is linear by construction, so its slope is the constant — the
    // one property of the instant curve the tuning holds fixed.
    instantSlope: () => params.instantScale,
    passiveSlope: (evo) => scenarioPassiveSlope(evo, params),
  };
}

/** The scenario at its shipped defaults, for anything outside the provider. */
export const DEFAULT_SCENARIO: Ruleset = makeScenarioRuleset(DEFAULT_SCENARIO_PARAMS);

export const RULESETS: Record<RulesetId, Ruleset> = {
  live: LIVE,
  testing3v3: TESTING_3V3,
  scenario: DEFAULT_SCENARIO,
};

/** Switch order — live first, since that is what the numbers default to. */
export const RULESET_ORDER: RulesetId[] = ['live', 'testing3v3', 'scenario'];

/**
 * Branches the switch offers with the tuner off. The scenario is not one of
 * them: without its controls on screen it would be an unexplained third curve
 * that nothing in the page can move.
 */
export const BASE_RULESET_ORDER: RulesetId[] = ['live', 'testing3v3'];

export const DEFAULT_RULESET_ID: RulesetId = 'live';

export function rulesetById(id: string | null | undefined): Ruleset {
  return (id && RULESETS[id as RulesetId]) || RULESETS[DEFAULT_RULESET_ID];
}
